"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdditionalPurchaseModal = void 0;
const react_1 = __importDefault(require("react"));
const react_2 = require("@chakra-ui/react");
function validQuote(quote) {
    return !!quote && !!quote.id && Number.isSafeInteger(quote.revision) && quote.revision > 0
        && !!quote.termsVersion && /^[1-9][0-9]*$/.test(quote.companyId) && /^[1-9][0-9]*$/.test(quote.branchId)
        && !!quote.planName && !!quote.unit && !!quote.billingNotice
        && Number.isSafeInteger(quote.quantity) && quote.quantity > 0
        && Number.isSafeInteger(quote.totalCents) && quote.totalCents >= 0 && Number.isFinite(Date.parse(quote.expiresAt));
}
function confirmationKey() {
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}
/** Render only for an explicit additional purchase, never during plan registration. */
const AdditionalPurchaseModal = props => {
    if (!props.isOpen)
        return null;
    // Changing any displayed quote term or permission discards credentials and prior acceptance.
    return react_1.default.createElement(PurchaseForm, Object.assign({ key: JSON.stringify([props.quote, props.authorized]) }, props));
};
exports.AdditionalPurchaseModal = AdditionalPurchaseModal;
const PurchaseForm = ({ quote, authorized, onClose, onConfirm, onConfirmed }) => {
    const [accepted, setAccepted] = react_1.default.useState(false);
    const [administrator, setAdministrator] = react_1.default.useState('');
    const [password, setPassword] = react_1.default.useState('');
    const [busy, setBusy] = react_1.default.useState(false);
    const [error, setError] = react_1.default.useState('');
    const [receipt, setReceipt] = react_1.default.useState(null);
    const [now, setNow] = react_1.default.useState(Date.now());
    const pending = react_1.default.useRef(false);
    const alive = react_1.default.useRef(true);
    const requestKey = react_1.default.useRef('');
    react_1.default.useEffect(() => {
        alive.current = true;
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => { alive.current = false; window.clearInterval(timer); };
    }, []);
    const available = authorized && validQuote(quote);
    const expired = !!quote && Date.parse(quote.expiresAt) <= now;
    const ready = available && !expired && accepted && administrator.trim() !== '' && password !== '' && !busy && !receipt;
    async function submit(event) {
        event.preventDefault();
        if (!ready || !quote || pending.current || Date.parse(quote.expiresAt) <= Date.now())
            return;
        pending.current = true;
        setBusy(true);
        setError('');
        setPassword('');
        let confirmed = false;
        try {
            if (!requestKey.current)
                requestKey.current = confirmationKey();
            const result = await onConfirm({ quoteId: quote.id, expectedRevision: quote.revision, termsVersion: quote.termsVersion,
                companyId: quote.companyId, branchId: quote.branchId, accepted: true, idempotencyKey: requestKey.current,
                administrator: administrator.trim(), password });
            if (!alive.current)
                return;
            if (!result || !result.id || result.status !== 'confirmed' || result.quoteId !== quote.id
                || result.companyId !== quote.companyId || result.branchId !== quote.branchId) {
                throw new Error('Unconfirmed receipt');
            }
            confirmed = true;
            setReceipt(result);
            setAdministrator('');
            if (onConfirmed)
                onConfirmed(result);
        }
        catch (_) {
            if (alive.current && !confirmed)
                setError('Não foi possível confirmar o adicional. Confira o resultado e informe a senha novamente para tentar.');
        }
        finally {
            pending.current = false;
            if (alive.current)
                setBusy(false);
        }
    }
    return react_1.default.createElement(react_2.Modal, { isOpen: true, onClose: onClose, closeOnEsc: !busy, closeOnOverlayClick: !busy, size: "lg" },
        react_1.default.createElement(react_2.ModalOverlay, null),
        react_1.default.createElement(react_2.ModalContent, { as: "form", onSubmit: submit, "aria-label": "Confirmar adicional" },
            react_1.default.createElement(react_2.ModalHeader, null, "Adicionar mais"),
            !busy && react_1.default.createElement(react_2.ModalCloseButton, null),
            react_1.default.createElement(react_2.ModalBody, null,
                react_1.default.createElement(react_2.Stack, { spacing: "4" },
                    receipt ? react_1.default.createElement(react_2.Alert, { status: "success" },
                        react_1.default.createElement(react_2.AlertIcon, null),
                        "Adicional confirmado. Registro ",
                        receipt.id,
                        ".") :
                        !authorized ? react_1.default.createElement(react_2.Alert, { status: "info" },
                            react_1.default.createElement(react_2.AlertIcon, null),
                            "A filial n\u00E3o possui autoriza\u00E7\u00E3o para liberar adicionais. Entre em contato com o respons\u00E1vel pelo plano.") :
                            !available || !quote ? react_1.default.createElement(react_2.Alert, { status: "info" },
                                react_1.default.createElement(react_2.AlertIcon, null),
                                "N\u00E3o h\u00E1 uma oferta de adicional dispon\u00EDvel.") : react_1.default.createElement(react_1.default.Fragment, null,
                                react_1.default.createElement(react_2.Text, null,
                                    quote.branchName || 'Filial ' + quote.branchId,
                                    " \u00B7 Empresa ",
                                    quote.companyId),
                                react_1.default.createElement(react_2.Text, { fontWeight: "bold" }, quote.planName),
                                react_1.default.createElement(react_2.Text, null,
                                    quote.quantity,
                                    " ",
                                    quote.unit,
                                    " \u2014 ",
                                    (quote.totalCents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })),
                                react_1.default.createElement(react_2.Text, null, quote.billingNotice),
                                expired ? react_1.default.createElement(react_2.Alert, { status: "warning" },
                                    react_1.default.createElement(react_2.AlertIcon, null),
                                    "Esta oferta expirou. Feche e solicite um novo resumo.") : react_1.default.createElement(react_1.default.Fragment, null,
                                    react_1.default.createElement(react_2.Checkbox, { isChecked: accepted, isDisabled: busy, onChange: event => setAccepted(event.target.checked) },
                                        "Confirmo a quantidade, o valor e as condi\u00E7\u00F5es acima \u2014 termos ",
                                        quote.termsVersion,
                                        "."),
                                    react_1.default.createElement(react_2.FormControl, { isRequired: true, isDisabled: busy },
                                        react_1.default.createElement(react_2.FormLabel, null, "E-mail do administrador"),
                                        react_1.default.createElement(react_2.Input, { type: "email", name: "administrator", autoComplete: "username", value: administrator, onChange: event => setAdministrator(event.target.value) })),
                                    react_1.default.createElement(react_2.FormControl, { isRequired: true, isDisabled: busy },
                                        react_1.default.createElement(react_2.FormLabel, null, "Senha do administrador"),
                                        react_1.default.createElement(react_2.Input, { type: "password", name: "administratorPassword", autoComplete: "off", value: password, onChange: event => setPassword(event.target.value) })))),
                    error && react_1.default.createElement(react_2.Alert, { status: "error" },
                        react_1.default.createElement(react_2.AlertIcon, null),
                        error))),
            react_1.default.createElement(react_2.ModalFooter, null,
                react_1.default.createElement(react_2.Button, { type: "button", variant: "ghost", mr: "3", isDisabled: busy, onClick: onClose }, receipt ? 'Fechar' : 'Cancelar'),
                !receipt && available && react_1.default.createElement(react_2.Button, { type: "submit", colorScheme: "blue", isDisabled: !ready, isLoading: busy }, "Confirmar adicional"))));
};
//# sourceMappingURL=index.js.map